import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus, Inbox, ClipboardList, FileText,
} from "lucide-react";

const ASSIGNER_ROLES = ["super_admin", "hr", "senior_management", "hod", "manager", "ea"];

const STATUS_TONE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  acknowledged: "bg-blue-50 text-blue-700 border-blue-200",
  started: "bg-cyan-50 text-cyan-700 border-cyan-200",
  in_progress: "bg-cyan-50 text-cyan-700 border-cyan-200",
  extended: "bg-orange-50 text-orange-700 border-orange-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  reviewed: "bg-emerald-50 text-emerald-800 border-emerald-300",
  task_over: "bg-slate-100 text-slate-700 border-slate-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  shifted: "bg-purple-50 text-purple-700 border-purple-200",
};
const PRIORITY_TONE = {
  urgent: "text-red-700 bg-red-100",
  high: "text-orange-700 bg-orange-100",
  medium: "text-cyan-700 bg-cyan-100",
  low: "text-slate-600 bg-slate-100",
};

function fmtDate(d) { if (!d) return "—"; return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }

export default function DelegationList() {
  const [me, setMe] = useState(null);
  const [scope, setScope] = useState("assigned-to-me");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setMe(data);
      } catch { /* ignore */ }
    })();
  }, []);

  const canAssign = me && ASSIGNER_ROLES.includes(me.role);
  // Rev. 2026-02 — Include hr_admin & hr_maker (maker-checker roles).
  const isAdmin = me && ["super_admin", "hr", "hr_admin", "hr_maker"].includes(me.role);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = { scope };
      if (filterStatus) params.status = filterStatus;
      const { data } = await api.get("/delegation/tasks", { params });
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [scope, filterStatus]);

  return (
    <div className="space-y-4" data-testid="delegation-list">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Delegation</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            {scope === "assigned-to-me" ? "My Tasks"
              : scope === "assigned-by-me" ? "Tasks I Assigned"
              : scope === "team" ? "Team Tasks"
              : "All Tasks"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {scope === "assigned-to-me"
              ? "Tasks delegated to you — acknowledge, update progress, complete."
              : "Track tasks you have delegated."}
          </p>
        </div>
        {canAssign && (
          <Button asChild className="brand-bg brand-bg-hover text-white" data-testid="new-task-btn">
            <Link to="/delegation/create"><Plus className="h-4 w-4 mr-2" /> New Task</Link>
          </Button>
        )}
      </div>

      {/* Scope tabs */}
      <Tabs value={scope} onValueChange={setScope}>
        <TabsList data-testid="scope-tabs">
          <TabsTrigger value="assigned-to-me" data-testid="tab-inbox">
            <Inbox className="h-3.5 w-3.5 mr-1.5" /> Assigned to me
          </TabsTrigger>
          {canAssign && (
            <TabsTrigger value="assigned-by-me" data-testid="tab-outbox">
              <ClipboardList className="h-3.5 w-3.5 mr-1.5" /> Assigned by me
            </TabsTrigger>
          )}
          {canAssign && (
            <TabsTrigger value="team" data-testid="tab-team">
              <ClipboardList className="h-3.5 w-3.5 mr-1.5" /> Team
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="all" data-testid="tab-all">
              <ClipboardList className="h-3.5 w-3.5 mr-1.5" /> All
            </TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {/* Status filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-wider text-slate-500">Status:</span>
        {["", "pending", "acknowledged", "in_progress", "extended", "completed", "task_over"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilterStatus(s)}
            className={`px-2 py-1 rounded text-[11px] font-medium border ${
              filterStatus === s
                ? "border-red-400 bg-red-50/40 brand-text"
                : "border-slate-200 bg-white text-slate-600"
            }`}
            data-testid={`filter-${s || "all"}`}
          >
            {s ? s.replace("_", " ") : "All"}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-sm text-slate-500 py-8 text-center">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-red-100 rounded-xl p-10 text-center" data-testid="empty-state">
          <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <div className="text-sm text-slate-600 font-medium">No tasks</div>
          <div className="text-xs text-slate-400 mt-1">
            {scope === "assigned-to-me"
              ? "You don't have any tasks assigned yet."
              : scope === "assigned-by-me"
              ? "You haven't delegated any tasks yet."
              : scope === "team"
              ? "No tasks in your team."
              : "No tasks found."}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-red-100 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="tasks-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Task #</th>
                <th className="text-left px-4 py-2">Title</th>
                <th className="text-left px-4 py-2">{scope === "assigned-to-me" ? "From" : "To"}</th>
                <th className="text-left px-4 py-2">Priority</th>
                <th className="text-left px-4 py-2">Deadline</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Progress</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr
                  key={t.id}
                  className="border-t border-slate-100 hover:bg-red-50/30 cursor-pointer"
                  onClick={() => navigate(`/delegation/tasks/${t.id}`)}
                  data-testid={`task-row-${t.id}`}
                >
                  <td className="px-4 py-2 text-xs font-mono text-slate-500">{t.task_number}</td>
                  <td className="px-4 py-2">
                    <div className="font-medium text-slate-900">{t.title}</div>
                    {t.category && <div className="text-[10px] text-slate-500">{t.category}</div>}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">
                    {scope === "assigned-to-me" ? (
                      <>
                        {t.assigned_by_name}
                        {t.on_behalf_of_name && (
                          <div className="text-[10px] text-slate-400">on behalf of {t.on_behalf_of_name}</div>
                        )}
                      </>
                    ) : t.assigned_to_name}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${PRIORITY_TONE[t.priority]}`}>
                      {t.priority}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs">{fmtDate(t.target_finish_date)}</td>
                  <td className="px-4 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${STATUS_TONE[t.status] || ""}`}>
                      {t.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right text-xs">
                    {t.progress_percentage || 0}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
